import { Link } from "react-router-dom";
import { Copy, Check, Star } from "lucide-react";
import { useState } from "react";
import { fmtPct, shortAddr } from "../lib/format";
import { useWatchlist } from "../lib/watchlist";

export const Pct = ({ v, className = "" }) => (
  <span className={`num ${v > 0 ? "text-[#10B981]" : v < 0 ? "text-[#EF4444]" : "text-[#9CA3AF]"} ${className}`}>{fmtPct(v)}</span>
);

export const CopyBtn = ({ text, className = "" }) => {
  const [ok, setOk] = useState(false);
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setOk(true);
    setTimeout(() => setOk(false), 1200);
  };
  return (
    <button
      data-testid="copy-button"
      className={`text-[#6B7280] hover:text-[#F3F4F6] transition-colors ${className}`}
      onClick={copy}
    >
      {ok ? <Check size={13} className="text-[#10B981]" /> : <Copy size={13} />}
    </button>
  );
};

export const AddrLink = ({ addr, to, label, n = 4 }) => (
  <span className="inline-flex items-center gap-1.5">
    <Link to={to || `/wallet/${addr}`} onClick={(e) => e.stopPropagation()} className="num text-[13px] text-[#9CA3AF] hover:text-[#10B981] transition-colors" data-testid="address-link">
      {label || shortAddr(addr, n)}
    </Link>
    <CopyBtn text={addr} />
  </span>
);

export const DataBadge = ({ live = true, label }) => (
  <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm ${live ? "border-[#10B98140] text-[#10B981]" : "border-[#F59E0B40] text-[#F59E0B]"}`} data-testid={live ? "live-data-badge" : "degraded-data-badge"}>
    <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-[#10B981] animate-pulse" : "bg-[#F59E0B]"}`} />
    {label || (live ? "Live Onchain Data" : "Degraded — retrying")}
  </span>
);

export const TokenLogo = ({ symbol, icon, size = 24 }) => {
  const [err, setErr] = useState(false);
  if (icon && !err) {
    return <img src={icon} alt={symbol} width={size} height={size} className="rounded-full shrink-0 bg-[#1A1A1A]" onError={() => setErr(true)} />;
  }
  const hue = [...(symbol || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return (
    <div className="flex items-center justify-center rounded-full font-heading font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.38, background: `hsl(${hue} 35% 16%)`, color: `hsl(${hue} 60% 62%)`, border: `1px solid hsl(${hue} 40% 26%)` }}>
      {(symbol || "?").slice(0, 2)}
    </div>
  );
};

export const WatchStar = ({ type, id, label, sub }) => {
  const { has, toggle } = useWatchlist();
  const active = has(type, id);
  return (
    <button
      data-testid={`watchlist-toggle-${id}`}
      onClick={(e) => { e.stopPropagation(); toggle({ type, id, label, sub }); }}
      className={`transition-colors ${active ? "text-[#F59E0B]" : "text-[#3F3F46] hover:text-[#9CA3AF]"}`}
    >
      <Star size={14} fill={active ? "currentColor" : "none"} />
    </button>
  );
};

export const StatCard = ({ label, value, sub, testId }) => (
  <div className="t-card p-3" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">{label}</div>
    <div className="num text-lg text-[#F3F4F6] mt-1">{value}</div>
    {sub && <div className="text-xs mt-0.5 text-[#6B7280]">{sub}</div>}
  </div>
);

export const SectionHead = ({ title, right, badge }) => (
  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
    <div className="flex items-center gap-2">
      <h2 className="font-heading font-bold text-sm md:text-base text-[#F3F4F6] uppercase tracking-wide">{title}</h2>
      {badge}
    </div>
    {right}
  </div>
);

export const Skel = ({ rows = 5, h = 32 }) => (
  <div className="space-y-1.5 p-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="bg-[#1A1A1A] rounded-sm animate-pulse" style={{ height: h }} />
    ))}
  </div>
);

export const Empty = ({ msg = "No data available" }) => (
  <div className="py-10 text-center text-[#6B7280] text-sm" data-testid="empty-state">{msg}</div>
);

export const ErrorState = ({ msg = "Live data temporarily unavailable — retrying automatically." }) => (
  <div className="py-4 px-3 text-center text-[#F59E0B] text-xs border border-[#F59E0B30] rounded-sm bg-[#F59E0B08]" data-testid="error-state">{msg}</div>
);
